import { FaroButtonSpinner } from "@components/common/button/faro-button-spinner";
import { Button, ButtonProps } from "@mui/material";
import { blue, neutral } from "@flat-ui";
import { PropsWithChildren, useMemo } from "react";

interface Props {
  /** Flag whether the button should show as loading */
  isLoading?: boolean;

  /** True when the button should be disabled */
  isDisabled?: boolean;

  /** Optional sx properties to override the button style. */
  sx?: ButtonProps["sx"];

  /** Callback when the button is clicked */
  onClick: () => void | Promise<void>;

  /** Data test ID for testing */
  dataTestId?: string;
}

/** Class name for the loading state */
const LOADING_CLASS_NAME = "loading";

/**
 * Exports a button that has a white background and just a blue border.
 * The button can be used with a loading property that will make the button
 * disabled and will show a spinner next to the text.
 */
export function FaroButtonContained({
  isLoading = false,
  isDisabled,
  sx = {},
  children,
  onClick,
  dataTestId,
}: PropsWithChildren<Props>): JSX.Element {
  /**
   * Flag whether the button should be shown as disabled, either because it is
   * disabled or because it is loading.
   */
  const shouldShowDisabled = useMemo(() => {
    return isLoading || isDisabled;
  }, [isDisabled, isLoading]);

  return (
    <Button
      variant="contained"
      className={isLoading ? LOADING_CLASS_NAME : ""}
      onClick={onClick}
      data-testid={dataTestId}
      disabled={shouldShowDisabled}
      disableFocusRipple={true}
      disableRipple={true}
      disableElevation={true}
      disableTouchRipple={true}
      draggable={false}
      sx={{
        fontSize: "14px",
        fontWeight: "600",
        backgroundColor: neutral[0],
        color: blue[500],
        borderRadius: "4px",
        border: `1px solid ${blue[500]}`,
        textTransform: "none",
        [`&.Mui-disabled.${LOADING_CLASS_NAME}`]: {
          color: blue[600],
          backgroundColor: neutral[200],
        },
        "&:hover": {
          backgroundColor: neutral[100],
          borderColor: blue[600],
          color: blue[600],
        },
        "&:active": {
          backgroundColor: neutral[200],
          borderColor: blue[600],
          color: blue[600],
        },
        ...sx,
      }}
    >
      {children}
      <FaroButtonSpinner
        shouldHide={!isLoading}
        loadingIndicatorColor={blue[600]}
        loadingTrackColor={neutral[100]}
      />
    </Button>
  );
}
